import { useCallback, useEffect, useState } from 'react';
import type { Pin, Memory } from '@/types';
import { subscribeUnlocks, useUnlockDetection } from '@/lib/unlock-detection';
import { useManifest } from '@/lib/queries';
import { UnlockCelebration } from './UnlockCelebration';

/**
 * Mounts at root once, downstream of the auth gate. Watches the manifest for
 * locked → unlocked transitions and queues celebrations.
 *
 * Multiple unlocks (e.g. server bumps two pins at the same minute) are shown
 * one at a time, FIFO. Each celebration auto-advances after dismissal.
 */
export function UnlockListener() {
  const { data: manifest } = useManifest();
  useUnlockDetection(manifest);

  const [queue, setQueue] = useState<Array<{ pin: Pin; memory?: Memory }>>([]);

  useEffect(() => {
    const unsub = subscribeUnlocks((e) => {
      setQueue((q) => {
        // Dedupe back-to-back duplicate events (StrictMode double-invoke,
        // realtime echo). Last 4 events suffice — we never queue thousands.
        const last = q[q.length - 1];
        const id = e.kind === 'pin' ? e.pin.id : e.memory.id;
        const lastId = last && (last.memory ? last.memory.id : last.pin.id);
        if (id === lastId) return q;
        return [...q, e.kind === 'pin' ? { pin: e.pin } : { pin: e.pin, memory: e.memory }];
      });
    });
    return unsub;
  }, []);

  const dismiss = useCallback(() => {
    setQueue((q) => q.slice(1));
  }, []);

  const head = queue[0] ?? null;

  return <UnlockCelebration item={head} onDismiss={dismiss} />;
}
